import { Injectable, Provider, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
    ATOM_FILTER_ROW_LABELS,
    AtomFilterRowLabels,
} from "@atomchat-io/ui-design-system";
import { TranslocoService } from "@jsverse/transloco";
import { catchError, map, of, switchMap } from "rxjs";

/**
 * Bridges the `atom-filter-row` / `atom-filter` internal labels
 * (`ATOM_FILTER_ROW_LABELS`) to Transloco.
 *
 * The organism only receives the visible trigger `[label]` per instance; the
 * "Clear filters" button text, the toolbar aria-label and the internal trigger
 * aria-label all come from this token. Each property is a getter that touches
 * the loaded-language signal and re-reads the translation, so labels stay in
 * sync on live language switches (`reRenderOnLangChange: true`).
 */
@Injectable({
    providedIn: "root",
})
export class AtomFilterRowLabelsService {
    private readonly translocoService = inject(TranslocoService);
    /**
     * Ticks once the active language's catalog is loaded, not on the switch
     * itself: `translate()` returns the raw key until then, and the
     * organism's `computed()` would keep it. A failed load ticks anyway, so
     * the getters never throw.
     */
    private readonly loadedLang = toSignal(
        this.translocoService.langChanges$.pipe(
            switchMap((lang) =>
                this.translocoService.load(lang).pipe(
                    map(() => lang),
                    catchError(() => of(lang)),
                ),
            ),
        ),
    );

    readonly labels: AtomFilterRowLabels = this.buildLabels();

    private buildLabels(): AtomFilterRowLabels {
        const loadedLang = this.loadedLang;
        const t = this.translocoService;
        return {
            get cleanFiltersButton(): string {
                loadedLang();
                return t.translate("atom_filter_row.clean_filters_button");
            },
            get rowAriaLabel(): string {
                loadedLang();
                return t.translate("atom_filter_row.row_aria_label");
            },
            get triggerAriaLabel(): string {
                loadedLang();
                return t.translate("atom_filter_row.trigger_aria_label");
            },
            get triggerLabel(): string {
                loadedLang();
                return t.translate("atom_filter_row.trigger_label");
            },
        };
    }
}

export const provideAtomFilterRowLabels = (): Provider => ({
    provide: ATOM_FILTER_ROW_LABELS,
    useFactory: (): AtomFilterRowLabels =>
        inject(AtomFilterRowLabelsService).labels,
});
